import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type ReactNode,
} from "react";
import {
  Activity,
  AlertTriangle,
  ArrowUpRight,
  Award,
  BarChart3,
  BellRing,
  BriefcaseBusiness,
  Building2,
  CalendarDays,
  CalendarClock,
  CheckCircle2,
  ChevronRight,
  CircleDollarSign,
  ClipboardCheck,
  Download,
  FileSpreadsheet,
  FilePenLine,
  Flame,
  Gauge,
  HeartHandshake,
  Home,
  ListChecks,
  MessageSquareText,
  PhoneCall,
  Plus,
  Search,
  ShieldCheck,
  Sparkles,
  GraduationCap,
  Target,
  Trophy,
  Upload,
  UserCheck,
  UsersRound,
  WalletCards,
  X,
} from "lucide-react";
import { api, useAuth } from "./auth";
import { UserAvatar } from "./UserAvatar";
import { useNavigate } from "react-router-dom";

type Row = Record<string, any>;
const money = (value: unknown) =>
  new Intl.NumberFormat("en-AE", {
    style: "currency",
    currency: "AED",
    maximumFractionDigits: 0,
  }).format(Number(value || 0));
const roleLabel = (role: string) =>
  role
    .replaceAll("_", " ")
    .toLowerCase()
    .replace(/\b\w/g, (x) => x.toUpperCase());

export function RoleDashboard() {
  const { user, csrfToken } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState<Row | null>(null);
  const [selectedTask, setSelectedTask] = useState<Row | null>(null);
  const [calendar, setCalendar] = useState<Row[]>([]);
  const [drilldown, setDrilldown] = useState<{title:string;items:Array<[string,string]>}|null>(null);
  const [error, setError] = useState("");
  useEffect(() => {
    Promise.all([api<Row>("/api/v1/enterprise/dashboard"),api<{data:Row[]}>("/api/v1/calendar/me")])
      .then(([dashboard,events])=>{setData(dashboard);setCalendar(events.data)})
      .catch((value) => setError(value.message));
  }, []);
  if (!data) return <Loading error={error} />;
  const m = data.metrics || {},
    performance = data.performance || {},
    leaderboard = (data.leaderboard || []) as Row[],
    tasks = (data.tasks || []) as Row[],
    trend = (performance.trend || []) as Row[],
    workingLeads = (data.workingLeads || []) as Row[],
    agentProfile = data.agent || {},
    targets = performance.targets || {},
    activitySummary = data.activitySummary || {},
    attendance = data.attendance || {},
    dealSummary = data.dealSummary || {},
    commission = data.commission || {},
    scoreBreakdown = performance.scoreBreakdown || {};
  const agentRank = Math.max(1, leaderboard.findIndex((person) => person.id === user?.id) + 1);
  const attendanceDays = Number(attendance.present_days || 0) + Number(attendance.absent_days || 0) + Number(attendance.leave_days || 0);
  const attendanceScore = attendanceDays
    ? Math.round((Number(attendance.present_days || 0) / attendanceDays) * 100)
    : 0;
  async function message(userId: string) {
    if (!csrfToken) return;
    const result = await api<{ id: string }>("/api/v1/chat/direct", csrfToken, {
      method: "POST",
      body: JSON.stringify({ userId }),
    });
    navigate(`/chat?channel=${result.id}`);
  }
  async function quickUpdate(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!csrfToken || !selectedTask) return;
    const d = Object.fromEntries(new FormData(event.currentTarget));
    await api(`/api/v1/enterprise/tasks/${selectedTask.id}`, csrfToken, {
      method: "PATCH",
      body: JSON.stringify({
        status: d.status,
        progress: Number(d.progress),
        comment: d.comment,
      }),
    });
    setSelectedTask(null);
    setData(await api<Row>("/api/v1/enterprise/dashboard"));
  }
  return (
    <div className="content enterprise-page">
      <section className="command-hero">
        <div>
          <span className="kicker light">
            Leads · Activity · Conversion · Performance
          </span>
          <h1>Agent performance dashboard</h1>
          <p>
            Your live score, sales funnel, customer activity, commissions and
            team position—calculated from operational records.
          </p>
        </div>
        <div className="hero-period">
          <CalendarDays />
          <div><strong>{new Date().toLocaleDateString("en-AE", { day: "2-digit", month: "short", year: "numeric" })}</strong><small>This month</small></div>
        </div>
      </section>
      <section className="metric-ribbon">
        <Metric
          icon={<Target />}
          label="Fresh leads"
          value={m.fresh || 0}
          detail="Added this month"
        />
        <Metric
          icon={<UsersRound />}
          label="Total lead pool"
          value={m.total || 0}
          detail={`${m.contacted || 0} contacted`}
        />
        <Metric
          icon={<Activity />}
          label="Calls this month"
          value={m.calls || 0}
          detail={`${m.meetings || 0} meetings`}
        />
        <Metric
          icon={<UsersRound />}
          label="Meetings this month"
          value={m.meetings || 0}
          detail={`${targets.meetings_target || 12} target`}
        />
        <Metric
          icon={<WalletCards />}
          label="Commission estimate"
          value={money(performance.estimatedCommission)}
          detail="2.5% of closed value"
        />
        <Metric
          icon={<BarChart3 />}
          label="Conversion"
          value={`${performance.conversionRate || 0}%`}
          detail="Leads converted to wins"
        />
      </section>
      <section className="agent-performance-overview">
        <article className="agent-identity-card">
          <UserAvatar userId={user?.id || ""} name={agentProfile.display_name || user?.displayName || "Agent"} available={Boolean(user?.avatarAvailable)} className="agent-photo" />
          <h2>{agentProfile.display_name || user?.displayName}</h2>
          <p>{agentProfile.job_title || roleLabel(agentProfile.role || user?.role || "Agent")}</p>
          <span>{agentProfile.team_name || agentProfile.department || "Realco Group"}</span>
          <div className="agent-score-ring" style={{ "--score": `${Math.min(100, Number(performance.score || 0)) * 3.6}deg` } as React.CSSProperties}>
            <strong><Animated value={Number(performance.score || 0)} /></strong><small>/100</small>
          </div>
          <b>{Number(performance.score || 0) >= 80 ? "Strong performer" : Number(performance.score || 0) >= 50 ? "Building momentum" : "Action required"}</b>
          <dl>
            <div><dt>Rank in company</dt><dd>#{agentRank}</dd></div>
            <div><dt>Deals since joining</dt><dd>{dealSummary.deals_since_joining || 0}</dd></div>
            <div><dt>Last deal closed</dt><dd>{dealSummary.last_deal_at ? new Date(dealSummary.last_deal_at).toLocaleDateString("en-AE") : "—"}</dd></div>
            <div><dt>Commission earned</dt><dd>{money(commission.entitlement)}</dd></div>
            <div><dt>Pipeline value</dt><dd>{money(dealSummary.pipeline_value)}</dd></div>
          </dl>
        </article>
        <article className="panel kpi-performance-board">
          <header><div><span className="kicker">KPI performance</span><h2>This month</h2></div><Trophy /></header>
          <div>
            {[
              ["Calls", m.calls || 0, targets.calls_target || 80, scoreBreakdown.calls || 0, <PhoneCall />],
              ["Meetings", m.meetings || 0, targets.meetings_target || 12, scoreBreakdown.meetings || 0, <UsersRound />],
              ["Conversion", performance.conversionRate || 0, 20, scoreBreakdown.conversion || 0, <BarChart3 />],
              ["Deals", m.won || 0, targets.deals_target || 2, scoreBreakdown.deals || 0, <Trophy />],
            ].map(([label, value, target, points, icon]) => {
              const percent = Math.min(100, Math.round((Number(value) / Math.max(1, Number(target))) * 100));
              return <button key={String(label)} onClick={() => setDrilldown({ title: `${label} KPI`, items: [["Current", String(value)], ["Target", String(target)], ["Achievement", `${percent}%`], ["Score", `${points} points`]] })}>
                {icon as ReactNode}<strong>{String(value)}</strong><small>/ {String(target)}</small>
                <i style={{ "--progress": `${percent * 3.6}deg` } as React.CSSProperties}><b>{percent}%</b></i>
                <span>{String(points)} points</span>
              </button>;
            })}
          </div>
        </article>
      </section>
      <section className="panel productivity-chart">
        <header>
          <div>
            <span className="kicker">Six-month productivity</span>
            <h2>Activity and conversions</h2>
          </div>
          <BarChart3 />
        </header>
        <div className="chart-bars" role="button" tabIndex={0} aria-label="Open six-month productivity detail" onClick={()=>setDrilldown({title:"Six-month productivity",items:trend.flatMap((point)=>[[`${point.label} · leads`,point.leads],[`${point.label} · calls`,point.calls],[`${point.label} · won`,point.won]])})} onKeyDown={(event)=>{if(event.key==="Enter"||event.key===" "){event.preventDefault();setDrilldown({title:"Six-month productivity",items:trend.flatMap((point)=>[[`${point.label} · leads`,point.leads],[`${point.label} · calls`,point.calls],[`${point.label} · won`,point.won]])})}}}>
          {trend.map((point, index) => {
            const ceiling = Math.max(
              1,
              ...trend.map((x) =>
                Math.max(Number(x.leads || 0), Number(x.calls || 0)),
              ),
            );
            return (
              <div key={`${point.label}-${index}`}>
                <div className="bar-pair">
                  <i
                    style={{
                      height: `${Math.max(5, (Number(point.leads || 0) / ceiling) * 100)}%`,
                    }}
                    title={`${point.leads} leads`}
                  />
                  <b
                    style={{
                      height: `${Math.max(5, (Number(point.calls || 0) / ceiling) * 100)}%`,
                    }}
                    title={`${point.calls} calls`}
                  />
                </div>
                <span>{point.label}</span>
                <small>{point.won} won</small>
              </div>
            );
          })}
        </div>
        <footer>
          <span>
            <i />
            Leads
          </span>
          <span>
            <b />
            Calls
          </span>
        </footer>
      </section>
      <section className="performance-deck">
        <article className="performance-score-card panel">
          <span className="kicker">Performance score</span>
          <div
            className="score-ring"
            style={
              {
                "--score": `${Math.min(100, Number(performance.score || 0)) * 3.6}deg`,
              } as React.CSSProperties
            }
          >
            <strong>
              <Animated value={Number(performance.score || 0)} />
            </strong>
            <small>/ 100</small>
          </div>
          <h3>
            {Number(performance.score || 0) >= 80
              ? "Strong performer"
              : Number(performance.score || 0) >= 50
                ? "Building momentum"
                : "Action required"}
          </h3>
          <p>
            {m.won || 0} deals · {money(m.won_value)} pipeline won
          </p>
        </article>
        <article className="pipeline-funnel panel" role="button" tabIndex={0} onClick={()=>setDrilldown({title:"Lead-to-deal funnel",items:[["Leads in scope",String(m.total||0)],["Contacts made",String(m.contacted||0)],["Meetings",String(m.meetings||0)],["Deals closed",String(m.won||0)]]})}>
          <header>
            <div>
              <span className="kicker">Lead-to-deal funnel</span>
              <h2>Conversion this month</h2>
            </div>
          </header>
          {[
            ["Leads in scope", Number(m.total || 0), 100],
            [
              "Contacts made",
              Number(m.contacted || 0),
              Number(m.total)
                ? (Number(m.contacted) / Number(m.total)) * 100
                : 0,
            ],
            [
              "Meetings",
              Number(m.meetings || 0),
              Number(m.total)
                ? (Number(m.meetings) / Number(m.total)) * 100
                : 0,
            ],
            [
              "Deals closed",
              Number(m.won || 0),
              Number(m.total) ? (Number(m.won) / Number(m.total)) * 100 : 0,
            ],
          ].map(([name, value, percent]) => (
            <div className="funnel-row" key={String(name)}>
              <span>{name}</span>
              <div>
                <i style={{ width: `${Math.max(2, Number(percent))}%` }} />
              </div>
              <strong>{value}</strong>
              <small>{Math.round(Number(percent))}%</small>
            </div>
          ))}
        </article>
        <article className="activity-summary panel">
          <header>
            <div>
              <span className="kicker">Activity summary</span>
              <h2>Live operating rhythm</h2>
            </div>
          </header>
          <div>
            <Metric
              icon={<Activity />}
              label="Calls"
              value={m.calls || 0}
              detail="This month"
            />
            <Metric
              icon={<UsersRound />}
              label="Meetings"
              value={m.meetings || 0}
              detail="Customer meetings"
            />
            <Metric
              icon={<Building2 />}
              label="Viewings"
              value={m.viewings || 0}
              detail="Property activity"
            />
            <Metric icon={<CalendarClock />} label="Follow-ups" value={activitySummary.followups || 0} detail="Completed actions" />
            <Metric icon={<FilePenLine />} label="Offers" value={activitySummary.offers || 0} detail="Customer offers" />
            <Metric icon={<GraduationCap />} label="Trainings" value={activitySummary.trainings || 0} detail={`${targets.trainings_target || 1} target`} />
            <Metric icon={<Sparkles />} label="Events" value={activitySummary.events || 0} detail="Developer events" />
            <Metric
              icon={<BellRing />}
              label="Alerts"
              value={m.unread || 0}
              detail="Unread notifications"
            />
          </div>
        </article>
      </section>
      <section className="dashboard-intelligence-grid">
        <article className="panel score-breakdown"><header><div><span className="kicker">100 point system</span><h2>Score breakdown</h2></div><Award/></header>{[["Calls & activity",Number(scoreBreakdown.calls||0),Number(targets.score_calls_weight||40),"blue"],["Meetings",Number(scoreBreakdown.meetings||0),Number(targets.score_meetings_weight||30),"green"],["Conversion",Number(scoreBreakdown.conversion||0),Number(targets.score_conversion_weight||10),"violet"],["Deals closed",Number(scoreBreakdown.deals||0),Number(targets.score_deals_weight||20),"amber"]].map(([name,value,weight,tone])=><button key={String(name)} onClick={()=>setDrilldown({title:String(name),items:[["Points earned",String(value)],["Maximum points",String(weight)],["Period","Current month"]]})}><i className={String(tone)}/><span>{String(name)}</span><b>{String(value)}</b><small>/ {String(weight)} pts</small></button>)}</article>
        <article className="panel agent-calendar"><header><div><span className="kicker">My calendar</span><h2>Upcoming commitments</h2></div><CalendarClock/></header><div>{calendar.slice(0,7).map((event)=><button key={event.id} onClick={()=>setDrilldown({title:String(event.title),items:[["Starts",new Date(event.starts_at).toLocaleString("en-AE")],["Ends",new Date(event.ends_at).toLocaleString("en-AE")],["Location",String(event.location||"Not set")],["Status",roleLabel(String(event.status))]]})}><time><strong>{new Date(event.starts_at).toLocaleDateString("en-AE",{day:"2-digit"})}</strong><small>{new Date(event.starts_at).toLocaleDateString("en-AE",{month:"short"})}</small></time><div><strong>{event.title}</strong><small>{roleLabel(String(event.event_type))} · {new Date(event.starts_at).toLocaleTimeString("en-AE",{hour:"2-digit",minute:"2-digit"})}</small></div><ChevronRight/></button>)}</div>{!calendar.length&&<Empty text="Your viewings, calls, tasks and team events will appear here."/>}</article>
      </section>
      <section className="attendance-alert-grid">
        <article className="panel attendance-score-card">
          <header><div><span className="kicker">Attendance score</span><h2>This month</h2></div><UserCheck /></header>
          <div className="attendance-score"><strong>{attendanceScore}%</strong><i><b style={{ width: `${attendanceScore}%` }} /></i></div>
          <dl>
            <div><dt>Present days</dt><dd>{attendance.present_days || 0}</dd></div>
            <div><dt>Late arrivals</dt><dd>{attendance.late_arrivals || 0}</dd></div>
            <div><dt>Early leave</dt><dd>{attendance.early_leaves || 0}</dd></div>
            <div><dt>Leave days</dt><dd>{attendance.leave_days || 0}</dd></div>
          </dl>
        </article>
        <article className="panel performance-alerts">
          <header><div><span className="kicker">Alerts & notifications</span><h2>Action signals</h2></div><BellRing /></header>
          {[["Below call KPI",Math.max(0,Number(targets.calls_target||80)-Number(m.calls||0)),<PhoneCall/>],["Overdue lead actions",Number(m.overdue||0),<AlertTriangle/>],["Open tasks",tasks.length,<ListChecks/>],["Unread notifications",Number(m.unread||0),<BellRing/>]].map(([label,value,icon])=><button key={String(label)} onClick={()=>setDrilldown({title:String(label),items:[["Current count",String(value)],["Updated","Live"]]})}>{icon as ReactNode}<span>{String(label)}</span><strong>{String(value)}</strong><ChevronRight/></button>)}
        </article>
      </section>
      <div className="dashboard-grid">
        <section className="panel action-panel">
          <header>
            <div>
              <span className="kicker">Working list</span>
              <h2>Next actions</h2>
            </div>
            <a href="/modules/workflow">
              Open all <ChevronRight />
            </a>
          </header>
          <div>
            {tasks.map((task, index) => (
              <article
                key={task.id}
                className={task.important ? "important" : ""}
                role="button"
                tabIndex={0}
                onClick={() => setSelectedTask(task)}
                onKeyDown={(event) =>
                  event.key === "Enter" && setSelectedTask(task)
                }
              >
                <span className="rank">
                  {String(index + 1).padStart(2, "0")}
                </span>
                <div>
                  <strong>{task.title}</strong>
                  <small>
                    {task.priority} ·{" "}
                    {task.due_at
                      ? new Date(task.due_at).toLocaleString("en-AE")
                      : "No deadline"}
                  </small>
                  <div className="mini-progress">
                    <i style={{ width: `${task.progress || 0}%` }} />
                  </div>
                </div>
                <span>{task.progress || 0}%</span>
              </article>
            ))}
            {!tasks.length && <Empty text="You have no open actions." />}
          </div>
        </section>
        <section className="panel leaderboard">
          <header>
            <div>
              <span className="kicker">Live competition</span>
              <h2>Team leaderboard</h2>
            </div>
            <Award />
          </header>
          <div>
            {leaderboard.slice(0, 5).map((person, index) => {
              const score =
                Number(person.won || 0) * 10 +
                Number(person.calls || 0) +
                Number(person.leads || 0);
              return (
                <article key={person.id}>
                  <span className={`podium p${index + 1}`}>{index + 1}</span>
                  <div>
                    <strong>{person.display_name}</strong>
                    <small>
                      {person.won} wins · {person.calls} calls · {person.leads}{" "}
                      leads
                    </small>
                    <div className="scorebar">
                      <i style={{ width: `${Math.min(100, score)}%` }} />
                    </div>
                  </div>
                  <b>{score}</b>
                  {person.id !== user?.id && (
                    <button
                      className="leader-message"
                      aria-label={`Message ${person.display_name}`}
                      onClick={() => void message(person.id)}
                    >
                      <MessageSquareText size={16} />
                    </button>
                  )}
                </article>
              );
            })}
            {!leaderboard.length && (
              <Empty text="Assign agents to teams to start the leaderboard." />
            )}
          </div>
        </section>
        <section className="panel insight-card">
          <Sparkles />
          <div>
            <span className="kicker">Operational recommendation</span>
            <h2>
              {Number(m.overdue || 0) > 0
                ? "Recover overdue customer contact"
                : "Protect today’s momentum"}
            </h2>
            <p>
              {Number(m.overdue || 0) > 0
                ? `${m.overdue} leads have missed the first-contact target. Reallocate or contact them before adding new work.`
                : "Complete the highest-priority tasks, log every call and keep each customer stage current."}
            </p>
          </div>
        </section>
        <section className="panel control-health">
          <header>
            <span className="kicker">Control health</span>
            <h2>Business checks</h2>
          </header>
          <div>
            <Health
              label="Employee profiles"
              value={`${Math.max(0, Number(data.hr?.employees || 0) - Number(data.hr?.incomplete || 0))}/${data.hr?.employees || 0}`}
              ok={!Number(data.hr?.incomplete || 0)}
            />
            <Health
              label="Expense approvals"
              value={String(data.expenses?.pending || 0)}
              ok={!Number(data.expenses?.pending || 0)}
            />
            <Health
              label="Platform invoices"
              value={String(data.platformFees?.due || 0)}
              ok={!Number(data.platformFees?.due || 0)}
            />
            <Health
              label="Unread alerts"
              value={String(m.unread || 0)}
              ok={!Number(m.unread || 0)}
            />
          </div>
        </section>
        <section className="panel working-leads">
          <header>
            <div>
              <span className="kicker">My live leads</span>
              <h2>24-hour working list</h2>
            </div>
            <Target />
          </header>
          {workingLeads.map((lead) => (
            <article key={lead.assignment_id}>
              <div>
                <strong>{lead.display_name}</strong>
                <small>
                  {lead.reference} · {roleLabel(lead.stage)}
                </small>
              </div>
              <span>
                {lead.auto_return_at
                  ? `${Math.max(0, Math.ceil((Date.parse(lead.auto_return_at) - Date.now()) / 3600000))}h left`
                  : "Active"}
              </span>
              <button onClick={() => navigate("/modules/crm")}>
                <ChevronRight />
              </button>
            </article>
          ))}
          {!workingLeads.length && (
            <Empty text="Claimed and allocated leads will appear here." />
          )}
        </section>
      </div>
      {selectedTask && (
        <div
          className="modal-backdrop"
          onMouseDown={(event) =>
            event.currentTarget === event.target && setSelectedTask(null)
          }
        >
          <form className="modal quick-task-modal" onSubmit={quickUpdate}>
            <header>
              <div>
                <span className="kicker">Working list update</span>
                <h2>{selectedTask.title}</h2>
              </div>
              <button
                type="button"
                className="icon-button"
                onClick={() => setSelectedTask(null)}
              >
                <X />
              </button>
            </header>
            <div className="form-grid">
              <label>
                Status
                <select name="status" defaultValue={selectedTask.status}>
                  <option>OPEN</option>
                  <option>IN_PROGRESS</option>
                  <option>BLOCKED</option>
                  <option>COMPLETED</option>
                </select>
              </label>
              <label>
                Progress
                <input
                  name="progress"
                  type="number"
                  min="0"
                  max="100"
                  defaultValue={selectedTask.progress || 0}
                />
              </label>
              <label className="span-2">
                Update comment
                <textarea
                  name="comment"
                  required
                  rows={4}
                  placeholder="What changed and what happens next?"
                />
              </label>
            </div>
            <footer>
              <button
                type="button"
                className="button secondary"
                onClick={() => setSelectedTask(null)}
              >
                Cancel
              </button>
              <button className="button primary">Save update</button>
            </footer>
          </form>
        </div>
      )}
      {drilldown&&<div className="modal-backdrop" onMouseDown={(event)=>event.currentTarget===event.target&&setDrilldown(null)}><section className="modal dashboard-drilldown"><header><div><span className="kicker">Live dashboard detail</span><h2>{drilldown.title}</h2></div><button className="icon-button" onClick={()=>setDrilldown(null)}><X/></button></header><div>{drilldown.items.map(([name,value])=><article key={name}><span>{name}</span><strong>{value}</strong></article>)}</div><footer><button className="button primary" onClick={()=>setDrilldown(null)}>Done</button></footer></section></div>}
    </div>
  );
}

export function LeadCommand() {
  const { csrfToken, user } = useAuth();
  const [rows, setRows] = useState<Row[]>([]),
    [claims, setClaims] = useState<Row[]>([]),
    [query, setQuery] = useState(""),
    [selected, setSelected] = useState<Row | null>(null),
    [detail, setDetail] = useState<Row | null>(null),
    [message, setMessage] = useState(""),
    [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  const canAllocate = [
    "SUPERADMIN",
    "GROUP_ADMIN",
    "COMPANY_ADMIN",
    "COO",
    "OWNER",
    "TEAM_LEAD",
  ].includes(user?.role || "");
  async function load() {
    try {
      const [leads, pending] = await Promise.all([
        api<{ data: Row[] }>("/api/v1/enterprise/leads"),
        canAllocate
          ? api<{ data: Row[] }>("/api/v1/enterprise/lead-claims")
          : Promise.resolve({ data: [] }),
      ]);
      setRows(leads.data);
      setClaims(pending.data);
    } catch (value) {
      setError(
        value instanceof Error ? value.message : "Unable to load pipeline",
      );
    }
  }
  useEffect(() => {
    void load();
  }, []);
  const filtered = useMemo(
    () =>
      rows.filter((row) =>
        Object.values(row).some((value) =>
          String(value ?? "")
            .toLowerCase()
            .includes(query.toLowerCase()),
        ),
      ),
    [rows, query],
  );
  async function open(row: Row) {
    setSelected(row);
    setDetail(null);
    try {
      setDetail(await api<Row>(`/api/v1/enterprise/leads/${row.id}`));
    } catch (value) {
      setMessage(
        value instanceof Error ? value.message : "Full profile is restricted",
      );
    }
  }
  async function claim(id: string) {
    if (!csrfToken) return;
    setBusy(true);
    try {
      await api(`/api/v1/enterprise/leads/${id}/claim`, csrfToken, {
        method: "POST",
        body: JSON.stringify({ reason: "Available and ready to contact" }),
      });
      setMessage("Claim sent to your team leader.");
      await load();
    } catch (value) {
      setMessage(value instanceof Error ? value.message : "Claim failed");
    } finally {
      setBusy(false);
    }
  }
  async function decide(id: string, decision: string) {
    if (!csrfToken) return;
    await api(`/api/v1/enterprise/lead-claims/${id}/decision`, csrfToken, {
      method: "POST",
      body: JSON.stringify({
        decision,
        reason:
          decision === "APPROVED"
            ? "Approved for immediate contact"
            : "Reallocation required",
      }),
    });
    await load();
  }
  async function importFile(file: File) {
    if (!csrfToken) return;
    setBusy(true);
    try {
      const response = await fetch("/api/v1/enterprise/leads/import", {
        method: "POST",
        credentials: "include",
        headers: {
          "x-csrf-token": csrfToken,
          "content-type":
            file.type ||
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        },
        body: file,
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.message || result.code);
      setMessage(
        `${result.created} leads imported; ${result.skipped} rows skipped.`,
      );
      await load();
    } catch (value) {
      setMessage(value instanceof Error ? value.message : "Import failed");
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="content enterprise-page">
      <section className="page-heading">
        <div>
          <span className="kicker">
            Permission-controlled customer acquisition
          </span>
          <h1>Sales Pipeline</h1>
          <p>
            Claim, allocate, enrich, contact and convert customers with contact
            masking and a retained history.
          </p>
        </div>
        <div className="heading-actions">
          <a
            className="button secondary"
            href="/api/v1/enterprise/leads/import-template"
          >
            <Download />
            Excel template
          </a>
          {canAllocate && (
            <label className="button primary file-button">
              <Upload />
              Bulk upload
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={(event) =>
                  event.target.files?.[0] &&
                  void importFile(event.target.files[0])
                }
              />
            </label>
          )}
        </div>
      </section>
      {message && <div className="notice-banner">{message}</div>}
      {error && <div className="form-error">{error}</div>}
      <section className="metric-ribbon">
        <Metric
          icon={<Flame />}
          label="Visible leads"
          value={rows.length}
          detail="Company and area scope"
        />
        <Metric
          icon={<UserCheck />}
          label="Unclaimed"
          value={rows.filter((x) => x.claim_status === "UNCLAIMED").length}
          detail="Contact details protected"
        />
        <Metric
          icon={<ClipboardCheck />}
          label="Claims waiting"
          value={claims.length}
          detail="Team-lead approval"
        />
        <Metric
          icon={<Trophy />}
          label="Won"
          value={rows.filter((x) => x.stage === "WON").length}
          detail="Closed customers"
        />
      </section>
      {claims.length > 0 && (
        <section className="panel claim-strip">
          <header>
            <h2>Claims awaiting your decision</h2>
          </header>
          <div>
            {claims.map((claim) => (
              <article key={claim.id}>
                <div>
                  <strong>{claim.display_name}</strong>
                  <small>
                    {claim.reference} · {claim.requested_by_name} ·{" "}
                    {claim.team_name}
                  </small>
                </div>
                <button onClick={() => void decide(claim.id, "REJECTED")}>
                  Reject
                </button>
                <button
                  className="approve"
                  onClick={() => void decide(claim.id, "APPROVED")}
                >
                  Approve
                </button>
              </article>
            ))}
          </div>
        </section>
      )}
      <section className="panel table-panel">
        <header>
          <div>
            <span className="kicker">Live allocation pool</span>
            <h2>Group and business leads</h2>
          </div>
          <label className="search-control">
            <Search />
            <input
              placeholder="Search area, budget, stage or name"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
            />
          </label>
        </header>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Customer</th>
                <th>Business</th>
                <th>Location</th>
                <th>Requirement</th>
                <th>Budget</th>
                <th>Owner</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((row) => (
                <tr
                  key={row.id}
                  onClick={() => void open(row)}
                  className="clickable-row"
                >
                  <td>
                    <strong>{row.display_name}</strong>
                    <small>
                      {row.reference} ·{" "}
                      {row.email || row.mobile || "Contact hidden until claim"}
                    </small>
                  </td>
                  <td>
                    <span className="status">
                      {roleLabel(row.business_module)}
                    </span>
                  </td>
                  <td>
                    {row.community ||
                      row.area ||
                      row.city ||
                      row.emirate ||
                      "UAE"}
                    <small>
                      {row.property_type || "Qualification pending"}
                    </small>
                  </td>
                  <td>
                    {row.transaction_type || "—"}
                    <small>
                      {row.stage} · {row.temperature}
                    </small>
                  </td>
                  <td>{money(row.budget_max || row.budget_min)}</td>
                  <td>
                    {row.assigned_user_name || "Unassigned"}
                    <small>{row.assigned_team_name || "Open pool"}</small>
                  </td>
                  <td>
                    {!row.assigned_user_id && user?.role === "AGENT" ? (
                      <button
                        className="table-action"
                        disabled={busy}
                        onClick={(event) => {
                          event.stopPropagation();
                          void claim(row.id);
                        }}
                      >
                        Claim lead
                      </button>
                    ) : (
                      <ChevronRight />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!filtered.length && (
            <Empty text="No leads match the current search." />
          )}
        </div>
      </section>
      {selected && (
        <LeadProfile
          row={selected}
          result={detail}
          message={message}
          csrfToken={csrfToken}
          onClose={() => {
            setSelected(null);
            setDetail(null);
            setMessage("");
          }}
        />
      )}
    </div>
  );
}

function LeadProfile({
  row,
  result,
  message,
  csrfToken,
  onClose,
}: {
  row: Row;
  result: Row | null;
  message: string;
  csrfToken: string | null;
  onClose: () => void;
}) {
  const data = result?.data;
  const [comment, setComment] = useState(""),
    [activity, setActivity] = useState(""),
    [stage, setStage] = useState("");
  async function updateStage() {
    if (!csrfToken || !stage) return;
    await api(`/api/v1/enterprise/leads/${row.id}`, csrfToken, {
      method: "PATCH",
      body: JSON.stringify({ stage }),
    });
    setStage("");
  }
  async function logCall() {
    if (!csrfToken || !activity.trim()) return;
    await api(`/api/v1/enterprise/leads/${row.id}/activities`, csrfToken, {
      method: "POST",
      body: JSON.stringify({
        activityType: "CALL",
        subject: "Customer call",
        body: activity,
        outcome: "FOLLOW_UP_REQUIRED",
      }),
    });
    setActivity("");
  }
  async function addComment() {
    if (!csrfToken || !comment.trim()) return;
    await api(`/api/v1/enterprise/leads/${row.id}/comments`, csrfToken, {
      method: "POST",
      body: JSON.stringify({ body: comment }),
    });
    setComment("");
  }
  return (
    <div
      className="drawer-backdrop"
      onMouseDown={(event) => event.target === event.currentTarget && onClose()}
    >
      <aside className="record-drawer enterprise-drawer">
        <header>
          <div>
            <span className="kicker">{row.reference}</span>
            <h2>{row.display_name}</h2>
            <p>
              {data
                ? `${data.email || "No email"} · ${data.mobile || "No mobile"}`
                : "Protected customer profile"}
            </p>
          </div>
          <button className="icon-button" onClick={onClose}>
            <X />
          </button>
        </header>
        {!data ? (
          <section className="locked-profile">
            <ShieldCheck />
            <h3>Claim required</h3>
            <p>
              {message ||
                "Only the assigned agent, team leader and authorised senior staff can see the full customer profile."}
            </p>
          </section>
        ) : (
          <>
            <section className="profile-facts">
              <Fact label="Stage" value={data.stage} />
              <Fact
                label="Location"
                value={[data.community, data.city, data.emirate]
                  .filter(Boolean)
                  .join(", ")}
              />
              <Fact
                label="Budget"
                value={money(data.budget_max || data.budget_min)}
              />
              <Fact
                label="Last contact"
                value={
                  data.last_activity_at
                    ? new Date(data.last_activity_at).toLocaleString("en-AE")
                    : "Not contacted"
                }
              />
            </section>
            <section className="lead-quick-actions">
              <h3>Update customer</h3>
              <div>
                <select
                  value={stage}
                  onChange={(event) => setStage(event.target.value)}
                >
                  <option value="">Change stage…</option>
                  {[
                    "ATTEMPTING",
                    "CONTACTED",
                    "QUALIFIED",
                    "VIEWING",
                    "PROPOSAL",
                    "NEGOTIATION",
                    "WON",
                    "LOST",
                  ].map((value) => (
                    <option key={value}>{value}</option>
                  ))}
                </select>
                <button disabled={!stage} onClick={() => void updateStage()}>
                  Save stage
                </button>
              </div>
              <div className="comment-box">
                <textarea
                  value={activity}
                  onChange={(event) => setActivity(event.target.value)}
                  placeholder="Call outcome and next step…"
                />
                <button
                  disabled={!activity.trim()}
                  onClick={() => void logCall()}
                >
                  <Activity />
                  Log call
                </button>
              </div>
            </section>
            <section>
              <h3>Customer timeline</h3>
              <div className="timeline">
                {(result?.activities || []).map((item: Row) => (
                  <article key={item.id}>
                    <i />
                    <div>
                      <strong>{roleLabel(item.activity_type)}</strong>
                      <p>
                        {item.subject ||
                          item.body ||
                          item.outcome ||
                          "Activity recorded"}
                      </p>
                      <small>
                        {item.created_by_name} ·{" "}
                        {new Date(item.occurred_at).toLocaleString("en-AE")}
                      </small>
                    </div>
                  </article>
                ))}
                {!(result?.activities || []).length && (
                  <Empty text="No customer activity has been logged." />
                )}
              </div>
            </section>
            <section>
              <h3>Internal comments</h3>
              <div className="comment-box">
                <textarea
                  value={comment}
                  onChange={(event) => setComment(event.target.value)}
                  placeholder="Add a useful customer note…"
                />
                <button
                  disabled={!comment.trim()}
                  onClick={() => void addComment()}
                >
                  <MessageSquareText />
                  Add
                </button>
              </div>
              {(result?.comments || []).map((item: Row) => (
                <article className="comment" key={item.id}>
                  <strong>{item.created_by_name}</strong>
                  <p>{item.body}</p>
                </article>
              ))}
            </section>
          </>
        )}
      </aside>
    </div>
  );
}

export function TaskCommand() {
  const { csrfToken } = useAuth();
  const [rows, setRows] = useState<Row[]>([]),
    [open, setOpen] = useState(false),
    [message, setMessage] = useState("");
  async function load() {
    setRows((await api<{ data: Row[] }>("/api/v1/enterprise/tasks")).data);
  }
  useEffect(() => {
    void load();
  }, []);
  async function update(row: Row, progress: number) {
    if (!csrfToken) return;
    await api(`/api/v1/enterprise/tasks/${row.id}`, csrfToken, {
      method: "PATCH",
      body: JSON.stringify({
        progress,
        status: progress === 100 ? "COMPLETED" : "IN_PROGRESS",
        comment: `Progress updated to ${progress}%`,
      }),
    });
    await load();
  }
  return (
    <div className="content enterprise-page">
      <section className="page-heading">
        <div>
          <span className="kicker">Shared execution</span>
          <h1>Tasks & Reminders</h1>
          <p>
            Business tasks stay with the responsible team; personal tasks stay
            private to the assignee, creator and leadership.
          </p>
        </div>
        <button className="button primary" onClick={() => setOpen(true)}>
          <Plus />
          New task
        </button>
      </section>
      {message && <div className="notice-banner">{message}</div>}
      <section className="task-board">
        {["OPEN", "IN_PROGRESS", "BLOCKED", "COMPLETED"].map((status) => (
          <section key={status}>
            <header>
              <span>{roleLabel(status)}</span>
              <b>{rows.filter((x) => x.status === status).length}</b>
            </header>
            <div>
              {rows
                .filter((x) => x.status === status)
                .map((row) => (
                  <article
                    key={row.id}
                    className={row.important ? "important" : ""}
                  >
                    <div>
                      <span
                        className={`priority ${String(row.priority).toLowerCase()}`}
                      >
                        {row.priority}
                      </span>
                      <strong>{row.title}</strong>
                      <p>{row.description || "No description"}</p>
                      <small>
                        {row.assigned_user_name ||
                          row.assigned_team_name ||
                          "Business task"}{" "}
                        ·{" "}
                        {row.due_at
                          ? new Date(row.due_at).toLocaleString("en-AE")
                          : "No due date"}
                      </small>
                    </div>
                    <div className="progress-control">
                      <span>
                        <i style={{ width: `${row.progress}%` }} />
                      </span>
                      <select
                        value={row.progress}
                        onChange={(event) =>
                          void update(row, Number(event.target.value))
                        }
                      >
                        {[0, 25, 50, 75, 100].map((value) => (
                          <option key={value} value={value}>
                            {value}%
                          </option>
                        ))}
                      </select>
                    </div>
                  </article>
                ))}
            </div>
          </section>
        ))}
      </section>
      {open && (
        <TaskModal
          csrfToken={csrfToken}
          onClose={() => setOpen(false)}
          onSaved={() => {
            setOpen(false);
            setMessage("Task created and the assignee was notified.");
            void load();
          }}
        />
      )}
    </div>
  );
}

function TaskModal({
  csrfToken,
  onClose,
  onSaved,
}: {
  csrfToken: string | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [busy, setBusy] = useState(false),
    [people, setPeople] = useState<Row[]>([]),
    [teams, setTeams] = useState<Row[]>([]);
  useEffect(() => {
    api<{ users: Row[]; teams: Row[] }>(
      "/api/v1/enterprise/task-assignees",
    ).then((x) => {
      setPeople(x.users);
      setTeams(x.teams);
    });
  }, []);
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!csrfToken) return;
    const d = Object.fromEntries(new FormData(event.currentTarget));
    setBusy(true);
    try {
      await api("/api/v1/enterprise/tasks", csrfToken, {
        method: "POST",
        body: JSON.stringify({
          title: d.title,
          description: d.description,
          businessModule: d.businessModule,
          visibility: d.visibility,
          dueAt: d.dueAt ? new Date(String(d.dueAt)).toISOString() : null,
          priority: d.priority,
          assignedUserId: d.assignedUserId || null,
          assignedTeamId: d.assignedTeamId || null,
          important: d.important === "on",
        }),
      });
      onSaved();
    } finally {
      setBusy(false);
    }
  }
  return (
    <Modal
      title="Create task"
      eyebrow="Visible to the correct people"
      onClose={onClose}
    >
      <form onSubmit={submit}>
        <div className="form-grid">
          <label>
            Title
            <input name="title" required minLength={2} />
          </label>
          <label>
            Business
            <select name="businessModule">
              <option>REAL_ESTATE</option>
              <option>CAR_RENTAL</option>
              <option>BUSINESS_CENTER</option>
              <option>CLEANING</option>
              <option>MAINTENANCE</option>
              <option>OFFICE</option>
              <option>GROUP</option>
            </select>
          </label>
          <label>
            Assign to person
            <select name="assignedUserId">
              <option value="">No individual</option>
              {people.map((person) => (
                <option value={person.id} key={person.id}>
                  {person.display_name} · {roleLabel(person.role)}
                </option>
              ))}
            </select>
          </label>
          <label>
            Assign to team
            <select name="assignedTeamId">
              <option value="">No team</option>
              {teams.map((team) => (
                <option value={team.id} key={team.id}>
                  {team.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            Visibility
            <select name="visibility">
              <option value="ASSIGNEE">Individual / creator / leader</option>
              <option value="BUSINESS">
                Everyone responsible for business
              </option>
            </select>
          </label>
          <label>
            Priority
            <select name="priority">
              <option>NORMAL</option>
              <option>HIGH</option>
              <option>URGENT</option>
              <option>LOW</option>
            </select>
          </label>
          <label>
            Due date
            <input name="dueAt" type="datetime-local" />
          </label>
          <label className="check">
            <input name="important" type="checkbox" />
            Persistent important reminder
          </label>
          <label className="span-2">
            Details
            <textarea name="description" rows={4} />
          </label>
        </div>
        <footer>
          <button type="button" className="button secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="button primary" disabled={busy}>
            Create task
          </button>
        </footer>
      </form>
    </Modal>
  );
}

export function PeopleCommand() {
  const { csrfToken } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState<Row[]>([]),
    [countries, setCountries] = useState<Row[]>([]),
    [selected, setSelected] = useState<Row | null>(null),
    [detail, setDetail] = useState<Row | null>(null),
    [editing, setEditing] = useState(false),
    [query, setQuery] = useState(""),
    [company, setCompany] = useState("ALL"),
    [error, setError] = useState("");
  useEffect(() => {
    Promise.all([
      api<{ data: Row[] }>("/api/v1/enterprise/admin/profile-completion"),
      api<{ data: Row[] }>("/api/v1/enterprise/options/COUNTRY"),
    ])
      .then(([people, countryOptions]) => {
        setRows(people.data);
        setCountries(countryOptions.data);
      })
      .catch((value) =>
        setError(
          value instanceof Error ? value.message : "Unable to load people",
        ),
      );
  }, []);
  const average = rows.length
    ? Math.round(
        rows.reduce((sum, row) => sum + Number(row.completion), 0) /
          rows.length,
      )
    : 0;
  const companies = Array.from(
    new Set(rows.map((row) => row.trade_name || "Group")),
  ).sort();
  const visibleRows = rows.filter(
    (row) =>
      (company === "ALL" || (row.trade_name || "Group") === company) &&
      `${row.display_name} ${row.email} ${row.job_title || ""} ${row.department || ""}`
        .toLowerCase()
        .includes(query.toLowerCase()),
  );
  async function open(row: Row) {
    setSelected(row);
    setDetail(null);
    try {
      setDetail(
        (
          await api<{ data: Row }>(
            `/api/v1/enterprise/admin/users/${row.id}/profile`,
          )
        ).data,
      );
    } catch (value) {
      setError(
        value instanceof Error ? value.message : "Profile could not be opened",
      );
    }
  }
  async function saveProfile(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!csrfToken || !detail) return;
    const d = Object.fromEntries(new FormData(event.currentTarget));
    try {
      await api(
        `/api/v1/enterprise/admin/users/${detail.id}/profile`,
        csrfToken,
        {
          method: "PATCH",
          body: JSON.stringify({
            displayName: d.displayName,
            phone: d.phone || null,
            jobTitle: d.jobTitle || null,
            department: d.department || null,
            employeeNumber: d.employeeNumber || null,
            nationality: d.nationality || null,
            employmentType: d.employmentType || null,
            employmentStartDate: d.employmentStartDate || null,
            bio: d.bio || null,
            languages: String(d.languages || "")
              .split(",")
              .map((x) => x.trim())
              .filter(Boolean),
            specialisms: String(d.specialisms || "")
              .split(",")
              .map((x) => x.trim())
              .filter(Boolean),
          }),
        },
      );
      setEditing(false);
      await open(selected!);
    } catch (value) {
      setError(
        value instanceof Error ? value.message : "Profile could not be saved",
      );
    }
  }
  return (
    <div className="content enterprise-page">
      <section className="page-heading">
        <div>
          <span className="kicker">HR people intelligence</span>
          <h1>People & Compliance</h1>
          <p>
            Employee performance, profile completion, identity documents and
            follow-up actions in one accountable list.
          </p>
        </div>
        <button
          className="button primary"
          onClick={() => navigate("/admin?view=users&create=1")}
        >
          <Plus />
          Add employee
        </button>
      </section>
      {error && <div className="form-error">{error}</div>}
      <section className="metric-ribbon">
        <Metric
          icon={<UsersRound />}
          label="Employees"
          value={rows.length}
          detail="Active workforce"
        />
        <Metric
          icon={<Gauge />}
          label="Average completion"
          value={`${average}%`}
          detail="Profiles and documents"
        />
        <Metric
          icon={<AlertTriangle />}
          label="Needs follow-up"
          value={rows.filter((x) => x.completion < 80).length}
          detail="Below 80%"
        />
        <Metric
          icon={<CheckCircle2 />}
          label="Complete"
          value={rows.filter((x) => x.completion === 100).length}
          detail="Ready files"
        />
      </section>
      <section className="people-toolbar">
        <label>
          <Search />
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search people, role, department…"
          />
        </label>
        <select
          value={company}
          onChange={(event) => setCompany(event.target.value)}
        >
          <option value="ALL">All companies</option>
          {companies.map((name) => (
            <option key={name}>{name}</option>
          ))}
        </select>
        <span>{visibleRows.length} people</span>
      </section>
      <section className="people-grid people-directory">
        {visibleRows.map((row) => (
          <article key={row.id}>
            <UserAvatar
              userId={row.id}
              name={row.display_name}
              available={row.avatar_available}
              className="person-avatar"
            />
            <div>
              <strong>{row.display_name}</strong>
              <small>
                {row.job_title || roleLabel(row.role)} ·{" "}
                {row.trade_name || "Group"}
              </small>
              <div className="completion">
                <i style={{ width: `${row.completion}%` }} />
                <span>{row.completion}%</span>
              </div>
            </div>
            <div className="people-card-actions">
              <button
                aria-label={`Open ${row.display_name} profile`}
                onClick={() => void open(row)}
              >
                View profile <ChevronRight />
              </button>
              <button
                onClick={() => {
                  void open(row);
                  setEditing(true);
                }}
              >
                Edit
              </button>
            </div>
          </article>
        ))}
      </section>
      {selected && (
        <div
          className="drawer-backdrop"
          onMouseDown={(event) =>
            event.target === event.currentTarget && setSelected(null)
          }
        >
          <aside className="record-drawer enterprise-drawer people-profile">
            <header>
              <div>
                <span className="kicker">Employee profile</span>
                <h2>{selected.display_name}</h2>
                <p>{selected.email}</p>
              </div>
              <button className="icon-button" onClick={() => setSelected(null)}>
                <X />
              </button>
            </header>
            {!detail ? (
              <div className="empty">Loading employee record…</div>
            ) : (
              <>
                <div className="drawer-toolbar">
                  <button
                    className="button primary small"
                    onClick={() => setEditing(!editing)}
                  >
                    {editing ? "Cancel edit" : "Edit employee"}
                  </button>
                </div>
                {editing && (
                  <section className="employee-edit">
                    <h3>Edit full employee profile</h3>
                    <form onSubmit={saveProfile}>
                      <div className="form-grid">
                        <label>
                          Full name
                          <input
                            name="displayName"
                            defaultValue={detail.display_name}
                            required
                          />
                        </label>
                        <label>
                          Mobile
                          <input
                            name="phone"
                            defaultValue={detail.phone || ""}
                          />
                        </label>
                        <label>
                          Job title
                          <input
                            name="jobTitle"
                            defaultValue={detail.job_title || ""}
                          />
                        </label>
                        <label>
                          Department
                          <input
                            name="department"
                            defaultValue={detail.department || ""}
                          />
                        </label>
                        <label>
                          Employee number
                          <input
                            name="employeeNumber"
                            defaultValue={detail.employee_number || ""}
                          />
                        </label>
                        <label>
                          Nationality
                          <select
                            name="nationality"
                            defaultValue={detail.nationality || "AE"}
                          >
                            {countries.map((country) => (
                              <option key={country.code} value={country.code}>
                                {country.label_en}
                              </option>
                            ))}
                          </select>
                        </label>
                        <label>
                          Employment type
                          <select
                            name="employmentType"
                            defaultValue={detail.employment_type || "FULL_TIME"}
                          >
                            <option value="FULL_TIME">Full time</option>
                            <option value="PART_TIME">Part time</option>
                            <option value="CONTRACT">Contract</option>
                            <option value="CONSULTANT">Consultant</option>
                            <option value="INTERN">Intern</option>
                          </select>
                        </label>
                        <label>
                          Start date
                          <input
                            name="employmentStartDate"
                            type="date"
                            defaultValue={
                              detail.employment_start_date?.slice(0, 10) || ""
                            }
                          />
                        </label>
                        <label>
                          Languages
                          <input
                            name="languages"
                            defaultValue={(detail.languages || []).join(", ")}
                          />
                        </label>
                        <label>
                          Specialisms
                          <input
                            name="specialisms"
                            defaultValue={(detail.specialisms || []).join(", ")}
                          />
                        </label>
                        <label className="span-2">
                          Professional profile
                          <textarea
                            name="bio"
                            rows={4}
                            defaultValue={detail.bio || ""}
                          />
                        </label>
                      </div>
                      <button className="button primary">
                        Save employee profile
                      </button>
                    </form>
                  </section>
                )}
                <section className="people-profile-head">
                  <UserAvatar
                    userId={detail.id}
                    name={detail.display_name}
                    available={detail.avatar_available}
                    className="person-avatar large"
                  />
                  <div>
                    <h3>{detail.job_title || roleLabel(detail.role)}</h3>
                    <p>
                      {detail.trade_name || "Realco Group"} ·{" "}
                      {detail.department || "Department not recorded"}
                    </p>
                  </div>
                </section>
                <section className="profile-facts">
                  <Fact label="Mobile" value={detail.phone} />
                  <Fact
                    label="Employee number"
                    value={detail.employee_number}
                  />
                  <Fact label="Nationality" value={detail.nationality} />
                  <Fact
                    label="Employment type"
                    value={detail.employment_type}
                  />
                  <Fact
                    label="Start date"
                    value={detail.employment_start_date}
                  />
                  <Fact
                    label="Last login"
                    value={
                      detail.last_login_at
                        ? new Date(detail.last_login_at).toLocaleString("en-AE")
                        : "Never"
                    }
                  />
                </section>
                <section>
                  <h3>Teams</h3>
                  <div className="tag-row">
                    {detail.teams.map((team: Row) => (
                      <span key={team.id}>
                        {team.name} · {team.role}
                      </span>
                    ))}
                    {!detail.teams.length && (
                      <Empty text="No team assignment." />
                    )}
                  </div>
                </section>
                <section>
                  <h3>Documents & certifications</h3>
                  {detail.documents.map((document: Row) => (
                    <article className="people-document" key={document.id}>
                      <div>
                        <strong>{roleLabel(document.type)}</strong>
                        <small>{document.name}</small>
                      </div>
                      <span>{document.verificationStatus}</span>
                    </article>
                  ))}
                  {!detail.documents.length && (
                    <Empty text="No documents uploaded." />
                  )}
                </section>
                <section>
                  <h3>Professional profile</h3>
                  <p className="drawer-note">
                    {detail.bio || "No professional profile recorded."}
                  </p>
                </section>
              </>
            )}
          </aside>
        </div>
      )}
    </div>
  );
}

export function ExpenseCommand() {
  const { csrfToken, user } = useAuth();
  const [rows, setRows] = useState<Row[]>([]),
    [pnl, setPnl] = useState<Row | null>(null),
    [open, setOpen] = useState(false);
  const canApprove = [
    "SUPERADMIN",
    "GROUP_ADMIN",
    "COMPANY_ADMIN",
    "COO",
    "OWNER",
    "FINANCE",
  ].includes(user?.role || "");
  async function load() {
    const [e, p] = await Promise.all([
      api<{ data: Row[] }>("/api/v1/enterprise/expenses"),
      canApprove
        ? api<Row>("/api/v1/enterprise/finance/pnl")
        : Promise.resolve(null),
    ]);
    setRows(e.data);
    setPnl(p);
  }
  useEffect(() => {
    void load();
  }, []);
  async function decide(id: string, decision: string) {
    if (!csrfToken) return;
    await api(`/api/v1/enterprise/expenses/${id}/decision`, csrfToken, {
      method: "POST",
      body: JSON.stringify({
        decision,
        reason:
          decision === "APPROVED"
            ? "Approved for posting"
            : "Returned for correction",
      }),
    });
    await load();
  }
  return (
    <div className="content enterprise-page">
      <section className="page-heading">
        <div>
          <span className="kicker">Controlled spend to ledger</span>
          <h1>Expenses & P&amp;L</h1>
          <p>
            Every claim follows approval and approved expenditure posts as a
            balanced journal.
          </p>
        </div>
        <button className="button primary" onClick={() => setOpen(true)}>
          <Plus />
          New expense
        </button>
      </section>
      <section className="metric-ribbon">
        <Metric
          icon={<WalletCards />}
          label="My claims"
          value={rows.length}
          detail="All statuses"
        />
        <Metric
          icon={<CalendarClock />}
          label="Awaiting approval"
          value={
            rows.filter((x) =>
              ["SUBMITTED", "APPROVAL_PENDING"].includes(x.status),
            ).length
          }
          detail={money(
            rows
              .filter((x) =>
                ["SUBMITTED", "APPROVAL_PENDING"].includes(x.status),
              )
              .reduce((s, x) => s + Number(x.amount), 0),
          )}
        />
        <Metric
          icon={<CircleDollarSign />}
          label="Revenue"
          value={money(pnl?.summary?.revenue)}
          detail="Posted ledger"
        />
        <Metric
          icon={<BarChart3 />}
          label="Net profit"
          value={money(pnl?.summary?.netProfit)}
          detail={`Expenses ${money(pnl?.summary?.expenses)}`}
        />
      </section>
      <section className="panel table-panel">
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Claim</th>
                <th>Submitted by</th>
                <th>Category</th>
                <th>Date</th>
                <th>Amount</th>
                <th>Status</th>
                <th>Approver route</th>
                <th>Decision</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id}>
                  <td>
                    <strong>{row.reference}</strong>
                    <small>{row.description}</small>
                  </td>
                  <td>
                    {row.submitted_by_name}
                    <small>{row.trade_name}</small>
                  </td>
                  <td>
                    {row.category}
                    <small>{row.merchant || "—"}</small>
                  </td>
                  <td>
                    {new Date(row.expense_date).toLocaleDateString("en-AE")}
                  </td>
                  <td>{money(row.amount)}</td>
                  <td>
                    <span className="status">{row.status}</span>
                  </td>
                  <td>
                    {row.approved_by_name || row.approver_names}
                    <small>
                      {row.approved_at
                        ? `Decided ${new Date(row.approved_at).toLocaleDateString("en-AE")}`
                        : "Awaiting an eligible approver"}
                    </small>
                  </td>
                  <td>
                    {canApprove &&
                    ["SUBMITTED", "APPROVAL_PENDING"].includes(row.status) ? (
                      <div className="row-buttons">
                        <button onClick={() => void decide(row.id, "REJECTED")}>
                          Reject
                        </button>
                        <button onClick={() => void decide(row.id, "APPROVED")}>
                          Approve
                        </button>
                      </div>
                    ) : (
                      "—"
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      {open && (
        <ExpenseModal
          csrfToken={csrfToken}
          onClose={() => setOpen(false)}
          onSaved={() => {
            setOpen(false);
            void load();
          }}
        />
      )}
    </div>
  );
}

function ExpenseModal({
  csrfToken,
  onClose,
  onSaved,
}: {
  csrfToken: string | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!csrfToken) return;
    const d = Object.fromEntries(new FormData(event.currentTarget));
    await api("/api/v1/enterprise/expenses", csrfToken, {
      method: "POST",
      body: JSON.stringify({
        expenseDate: d.expenseDate,
        category: d.category,
        merchant: d.merchant,
        description: d.description,
        amount: Number(d.amount),
        vatAmount: Number(d.vatAmount || 0),
        currency: d.currency,
        paymentMethod: d.paymentMethod,
        costCentre: d.costCentre,
      }),
    });
    onSaved();
  }
  return (
    <Modal
      title="Submit expense"
      eyebrow="Approval and ledger workflow"
      onClose={onClose}
    >
      <form onSubmit={submit}>
        <div className="form-grid">
          <label>
            Expense date
            <input
              name="expenseDate"
              type="date"
              required
              defaultValue={new Date().toISOString().slice(0, 10)}
            />
          </label>
          <label>
            Category
            <select name="category">
              <option>TRAVEL</option>
              <option>FUEL</option>
              <option>MARKETING</option>
              <option>OFFICE</option>
              <option>MAINTENANCE</option>
              <option>OTHER</option>
            </select>
          </label>
          <label>
            Merchant
            <input name="merchant" />
          </label>
          <label>
            Payment method
            <select name="paymentMethod">
              <option>CASH</option>
              <option>CARD</option>
              <option>BANK_TRANSFER</option>
              <option>PERSONAL_CARD</option>
            </select>
          </label>
          <label>
            Amount
            <input
              name="amount"
              type="number"
              min="0.01"
              step="0.01"
              required
            />
          </label>
          <label>
            VAT amount
            <input name="vatAmount" type="number" min="0" step="0.01" />
          </label>
          <label>
            Currency
            <select name="currency">
              <option>AED</option>
              <option>USD</option>
              <option>GBP</option>
              <option>EUR</option>
            </select>
          </label>
          <label>
            Cost centre
            <input name="costCentre" />
          </label>
          <label className="span-2">
            Business purpose
            <textarea name="description" rows={3} required />
          </label>
        </div>
        <footer>
          <button type="button" className="button secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="button primary">Submit for approval</button>
        </footer>
      </form>
    </Modal>
  );
}

export function ServiceCommand() {
  const { csrfToken, user } = useAuth();
  const canPublish = [
    "SUPERADMIN",
    "GROUP_ADMIN",
    "COMPANY_ADMIN",
    "COO",
    "OWNER",
  ].includes(user?.role || "");
  const [rows, setRows] = useState<Row[]>([]),
    [open, setOpen] = useState(false),
    [editing, setEditing] = useState<Row | null>(null);
  async function load() {
    setRows(
      (await api<{ data: Row[] }>("/api/v1/enterprise/services/catalog")).data,
    );
  }
  useEffect(() => {
    void load();
  }, []);
  async function toggle(row: Row, field: "active" | "published") {
    if (!csrfToken) return;
    await api(`/api/v1/enterprise/services/catalog/${row.id}`, csrfToken, {
      method: "PATCH",
      body: JSON.stringify({ [field]: !row[field] }),
    });
    await load();
  }
  return (
    <div className="content enterprise-page">
      <section className="page-heading">
        <div>
          <span className="kicker">Website-ready service catalogue</span>
          <h1>Cleaning & Maintenance Services</h1>
          <p>
            Configurable services, prices, availability and online publication
            for customer booking.
          </p>
        </div>
        <button className="button primary" onClick={() => setOpen(true)}>
          <Plus />
          Add service
        </button>
      </section>
      <section className="catalog-grid">
        {rows.map((row) => (
          <article key={row.id}>
            <div className="catalog-icon">
              <BriefcaseBusiness />
            </div>
            <span className="status">{roleLabel(row.business_module)}</span>
            <h2>{row.name_en}</h2>
            <p>{row.description_en || "Service description pending."}</p>
            <strong>
              {money(row.base_price)}{" "}
              <small>· {roleLabel(row.pricing_model)}</small>
            </strong>
            <footer>
              <button onClick={() => setEditing(row)}>Edit</button>
              {canPublish && (
                <button onClick={() => void toggle(row, "published")}>
                  {row.published ? "Unpublish" : "Publish"}
                </button>
              )}
              <button onClick={() => void toggle(row, "active")}>
                {row.active ? "Deactivate" : "Activate"}
              </button>
            </footer>
          </article>
        ))}
      </section>
      {open && (
        <ServiceModal
          csrfToken={csrfToken}
          onClose={() => setOpen(false)}
          onSaved={() => {
            setOpen(false);
            void load();
          }}
        />
      )}
      {editing && (
        <ServiceEditModal
          row={editing}
          csrfToken={csrfToken}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void load();
          }}
        />
      )}
    </div>
  );
}

function ServiceEditModal({
  row,
  csrfToken,
  onClose,
  onSaved,
}: {
  row: Row;
  csrfToken: string | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [error, setError] = useState("");
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!csrfToken) return;
    const d = Object.fromEntries(new FormData(event.currentTarget));
    try {
      await api(`/api/v1/enterprise/services/catalog/${row.id}`, csrfToken, {
        method: "PATCH",
        body: JSON.stringify({
          nameEn: d.nameEn,
          descriptionEn: d.descriptionEn || null,
          pricingModel: d.pricingModel,
          basePrice: Number(d.basePrice),
          durationMinutes: Number(d.durationMinutes || 60),
          active: d.active === "on",
          published: d.published === "on",
        }),
      });
      onSaved();
    } catch (value) {
      setError(
        value instanceof Error ? value.message : "Service could not be updated",
      );
    }
  }
  return (
    <Modal
      title="Edit service"
      eyebrow="Catalogue and website"
      onClose={onClose}
    >
      <form onSubmit={submit}>
        {error && <div className="form-error">{error}</div>}
        <div className="form-grid">
          <label>
            Service name
            <input name="nameEn" defaultValue={row.name_en} required />
          </label>
          <label>
            Pricing model
            <select name="pricingModel" defaultValue={row.pricing_model}>
              {[
                "HOURLY",
                "PER_VISIT",
                "AREA",
                "PACKAGE",
                "SUBSCRIPTION",
                "CUSTOM",
              ].map((x) => (
                <option key={x}>{x}</option>
              ))}
            </select>
          </label>
          <label>
            Base price
            <input
              name="basePrice"
              type="number"
              min="0"
              step="0.01"
              defaultValue={row.base_price}
              required
            />
          </label>
          <label>
            Duration (minutes)
            <input
              name="durationMinutes"
              type="number"
              min="15"
              defaultValue={row.duration_minutes || 60}
            />
          </label>
          <label className="span-2">
            Description
            <textarea
              name="descriptionEn"
              rows={4}
              defaultValue={row.description_en || ""}
            />
          </label>
          <label className="check">
            <input name="active" type="checkbox" defaultChecked={row.active} />
            Active
          </label>
          <label className="check">
            <input
              name="published"
              type="checkbox"
              defaultChecked={row.published}
            />
            Published on website
          </label>
        </div>
        <footer>
          <button type="button" className="button secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="button primary">Save changes</button>
        </footer>
      </form>
    </Modal>
  );
}

export function OfficeCommand() {
  const { csrfToken } = useAuth();
  const [rows, setRows] = useState<Row[]>([]),
    [open, setOpen] = useState(false),
    [error, setError] = useState("");
  async function load() {
    try {
      setRows(
        (await api<{ data: Row[] }>("/api/v1/enterprise/office/assets")).data,
      );
    } catch (value) {
      setError(
        value instanceof Error
          ? value.message
          : "Office assets could not be loaded",
      );
    }
  }
  useEffect(() => {
    void load();
  }, []);
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!csrfToken) return;
    const d = Object.fromEntries(new FormData(event.currentTarget));
    await api("/api/v1/enterprise/office/assets", csrfToken, {
      method: "POST",
      body: JSON.stringify({
        assetTag: d.assetTag,
        name: d.name,
        category: d.category,
        serialNumber: d.serialNumber || undefined,
        location: d.location || undefined,
        purchaseDate: d.purchaseDate || undefined,
        warrantyExpiry: d.warrantyExpiry || undefined,
        condition: d.condition,
        acquisitionCost: d.acquisitionCost
          ? Number(d.acquisitionCost)
          : undefined,
        supplier: d.supplier || undefined,
        returnRequired: d.returnRequired === "on",
      }),
    });
    setOpen(false);
    await load();
  }
  return (
    <div className="content enterprise-page">
      <section className="page-heading">
        <div>
          <span className="kicker">Inventory accountability</span>
          <h1>Inventory Management</h1>
          <p>
            Equipment, SIMs, phones, access devices and furniture with
            assignment, return, condition and warranty history.
          </p>
        </div>
        <button className="button primary" onClick={() => setOpen(true)}>
          <Plus />
          Add asset
        </button>
      </section>
      {error && <div className="form-error">{error}</div>}
      <section className="metric-ribbon">
        <Metric
          icon={<BriefcaseBusiness />}
          label="Registered assets"
          value={rows.length}
          detail="Company scope"
        />
        <Metric
          icon={<CheckCircle2 />}
          label="Active"
          value={rows.filter((x) => x.status === "ACTIVE").length}
          detail="Available or assigned"
        />
        <Metric
          icon={<UserCheck />}
          label="Assigned"
          value={rows.filter((x) => x.assigned_user_id).length}
          detail="Employee accountable"
        />
        <Metric
          icon={<CalendarClock />}
          label="Warranty due"
          value={
            rows.filter(
              (x) =>
                x.warranty_expiry &&
                Date.parse(x.warranty_expiry) < Date.now() + 7776000000,
            ).length
          }
          detail="Within 90 days"
        />
      </section>
      <section className="panel table-panel">
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Asset</th>
                <th>Category</th>
                <th>Location</th>
                <th>Assigned to</th>
                <th>Warranty</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id}>
                  <td>
                    <strong>{row.name}</strong>
                    <small>
                      {row.asset_tag} · {row.serial_number || "No serial"}
                    </small>
                  </td>
                  <td>{row.category || "General"}</td>
                  <td>{row.location || "Unallocated"}</td>
                  <td>{row.assigned_user_name || "Unassigned"}</td>
                  <td>{row.warranty_expiry || "Not recorded"}</td>
                  <td>
                    <span className="status">{row.status}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!rows.length && (
            <Empty text="No office assets have been registered." />
          )}
        </div>
      </section>
      {open && (
        <Modal
          title="Register inventory item"
          eyebrow="Inventory management"
          onClose={() => setOpen(false)}
        >
          <form onSubmit={submit}>
            <div className="form-grid">
              <label>
                Asset tag
                <input name="assetTag" required placeholder="CRBC-IT-001" />
              </label>
              <label>
                Name
                <input name="name" required />
              </label>
              <label>
                Category
                <select name="category">
                  <option value="LAPTOP">Laptop</option>
                  <option value="PHONE">Mobile phone</option>
                  <option value="SIM">SIM card</option>
                  <option value="ACCESS_CARD">Access card</option>
                  <option value="KEY">Key</option>
                  <option value="MONITOR">Monitor</option>
                  <option value="FURNITURE">Furniture</option>
                  <option value="TOOL">Tool</option>
                  <option value="OTHER">Other</option>
                </select>
              </label>
              <label>
                Serial number
                <input name="serialNumber" />
              </label>
              <label>
                Location
                <input name="location" placeholder="Reception / Office 145" />
              </label>
              <label>
                Purchase date
                <input name="purchaseDate" type="date" />
              </label>
              <label>
                Warranty expiry
                <input name="warrantyExpiry" type="date" />
              </label>
              <label>
                Condition
                <select name="condition" defaultValue="GOOD">
                  <option>NEW</option>
                  <option>GOOD</option>
                  <option>FAIR</option>
                  <option>DAMAGED</option>
                  <option>REPAIR</option>
                </select>
              </label>
              <label>
                Acquisition cost
                <input
                  name="acquisitionCost"
                  type="number"
                  min="0"
                  step="0.01"
                />
              </label>
              <label>
                Supplier
                <input name="supplier" />
              </label>
              <label className="check">
                <input name="returnRequired" type="checkbox" defaultChecked />
                Must be returned when access ends
              </label>
            </div>
            <footer>
              <button
                type="button"
                className="button secondary"
                onClick={() => setOpen(false)}
              >
                Cancel
              </button>
              <button className="button primary">Save asset</button>
            </footer>
          </form>
        </Modal>
      )}
    </div>
  );
}

function ServiceModal({
  csrfToken,
  onClose,
  onSaved,
}: {
  csrfToken: string | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!csrfToken) return;
    const d = Object.fromEntries(new FormData(event.currentTarget));
    await api("/api/v1/enterprise/services/catalog", csrfToken, {
      method: "POST",
      body: JSON.stringify({
        businessModule: d.businessModule,
        serviceCode: d.serviceCode,
        nameEn: d.nameEn,
        descriptionEn: d.descriptionEn,
        pricingModel: d.pricingModel,
        basePrice: Number(d.basePrice),
        currency: "AED",
        durationMinutes: Number(d.durationMinutes || 60),
        active: true,
        published: d.published === "on",
      }),
    });
    onSaved();
  }
  return (
    <Modal
      title="Add service"
      eyebrow="Cleaning or maintenance"
      onClose={onClose}
    >
      <form onSubmit={submit}>
        <div className="form-grid">
          <label>
            Business
            <select name="businessModule">
              <option>CLEANING</option>
              <option>MAINTENANCE</option>
              <option>INTERNET</option>
              <option>IT</option>
              <option>FIT_OUT</option>
            </select>
          </label>
          <label>
            Service code
            <input name="serviceCode" required placeholder="CLEAN-HOME-2H" />
          </label>
          <label>
            Service name
            <input name="nameEn" required />
          </label>
          <label>
            Pricing model
            <select name="pricingModel">
              <option>HOURLY</option>
              <option>PER_VISIT</option>
              <option>AREA</option>
              <option>PACKAGE</option>
              <option>SUBSCRIPTION</option>
              <option>CUSTOM</option>
            </select>
          </label>
          <label>
            Base price
            <input
              name="basePrice"
              type="number"
              min="0"
              step="0.01"
              required
            />
          </label>
          <label>
            Duration (minutes)
            <input
              name="durationMinutes"
              type="number"
              min="15"
              defaultValue="60"
            />
          </label>
          <label className="span-2">
            Description
            <textarea name="descriptionEn" rows={4} />
          </label>
          <label className="check span-2">
            <input name="published" type="checkbox" />
            Publish after authorised save
          </label>
        </div>
        <footer>
          <button type="button" className="button secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="button primary">Save service</button>
        </footer>
      </form>
    </Modal>
  );
}

export function OptionsCommand() {
  const { csrfToken } = useAuth();
  const [rows, setRows] = useState<Row[]>([]),
    [open, setOpen] = useState(false);
  async function load() {
    setRows(
      (await api<{ data: Row[] }>("/api/v1/enterprise/admin/options")).data,
    );
  }
  useEffect(() => {
    void load();
  }, []);
  const groups = Object.entries(
    rows.reduce<Record<string, Row[]>>((all, row) => {
      (all[row.category] ??= []).push(row);
      return all;
    }, {}),
  );
  return (
    <div className="content enterprise-page">
      <section className="page-heading">
        <div>
          <span className="kicker">Master data</span>
          <h1>Dropdowns & Buttons</h1>
          <p>
            One controlled source for selectable values used across every form
            and business.
          </p>
        </div>
        <button className="button primary" onClick={() => setOpen(true)}>
          <Plus />
          Add option
        </button>
      </section>
      <section className="option-groups">
        {groups.map(([category, items]) => (
          <article key={category}>
            <header>
              <strong>{roleLabel(category)}</strong>
              <span>{items.length}</span>
            </header>
            <div>
              {items.map((item) => (
                <span key={item.id}>{item.label_en}</span>
              ))}
            </div>
          </article>
        ))}
      </section>
      {open && (
        <Modal
          title="Add dropdown option"
          eyebrow="Administration"
          onClose={() => setOpen(false)}
        >
          <form
            onSubmit={async (event) => {
              event.preventDefault();
              if (!csrfToken) return;
              const d = Object.fromEntries(new FormData(event.currentTarget));
              await api("/api/v1/enterprise/admin/options", csrfToken, {
                method: "POST",
                body: JSON.stringify({
                  category: d.category,
                  code: d.code,
                  labelEn: d.labelEn,
                  labelAr: d.labelAr || undefined,
                  sortOrder: Number(d.sortOrder || 100),
                }),
              });
              setOpen(false);
              void load();
            }}
          >
            <div className="form-grid">
              <label>
                Category
                <input name="category" required placeholder="LEAD_SOURCE" />
              </label>
              <label>
                Code
                <input name="code" required pattern="[A-Z0-9_]+" />
              </label>
              <label>
                English label
                <input name="labelEn" required />
              </label>
              <label>
                Arabic label
                <input name="labelAr" dir="rtl" />
              </label>
              <label>
                Sort order
                <input name="sortOrder" type="number" defaultValue="100" />
              </label>
            </div>
            <footer>
              <button
                type="button"
                className="button secondary"
                onClick={() => setOpen(false)}
              >
                Cancel
              </button>
              <button className="button primary">Save option</button>
            </footer>
          </form>
        </Modal>
      )}
    </div>
  );
}

export function ReminderQueue() {
  const { csrfToken } = useAuth();
  const [rows, setRows] = useState<Row[]>([]);
  async function load() {
    try {
      setRows(
        (await api<{ data: Row[] }>("/api/v1/enterprise/reminders/due")).data,
      );
    } catch {}
  }
  useEffect(() => {
    void load();
    const timer = window.setInterval(() => void load(), 30000);
    return () => window.clearInterval(timer);
  }, []);
  const current = rows[0];
  if (!current) return null;
  async function action(kind: string) {
    if (!csrfToken) return;
    await api(`/api/v1/enterprise/reminders/${current.id}/action`, csrfToken, {
      method: "POST",
      body: JSON.stringify({ action: kind, snoozeMinutes: 15 }),
    });
    await load();
  }
  return (
    <aside
      className={`reminder-toast ${current.important ? "important" : ""}`}
      role="alert"
    >
      <div>
        <BellRing />
        <span>{current.important ? "Important reminder" : "Reminder"}</span>
      </div>
      <strong>{current.title}</strong>
      <p>{current.body}</p>
      <footer>
        {rows.length > 1 && <small>{rows.length - 1} queued</small>}
        <button onClick={() => void action("SNOOZE")}>15 min</button>
        <button onClick={() => void action("ACKNOWLEDGE")}>Done</button>
      </footer>
    </aside>
  );
}

function Metric({
  icon,
  label,
  value,
  detail,
}: {
  icon: ReactNode;
  label: string;
  value: ReactNode;
  detail: string;
}) {
  return (
    <article>
      {icon}
      <div>
        <span>{label}</span>
        <strong>{value}</strong>
        <small>{detail}</small>
      </div>
    </article>
  );
}
function Animated({ value }: { value: number }) {
  const [shown, setShown] = useState(0);
  const started = useRef(false);
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    const start = performance.now();
    function tick(now: number) {
      const p = Math.min(1, (now - start) / 700);
      setShown(Math.round(value * (1 - Math.pow(1 - p, 3))));
      if (p < 1) requestAnimationFrame(tick);
    }
    requestAnimationFrame(tick);
  }, [value]);
  return <>{shown}</>;
}
function Health({
  label,
  value,
  ok,
}: {
  label: string;
  value: string;
  ok: boolean;
}) {
  return (
    <article>
      <span className={ok ? "ok" : "warn"}>
        {ok ? <CheckCircle2 /> : <AlertTriangle />}
      </span>
      <strong>{label}</strong>
      <b>{value}</b>
    </article>
  );
}
function Fact({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div>
      <span>{label}</span>
      <strong>{value || "—"}</strong>
    </div>
  );
}
function Empty({ text }: { text: string }) {
  return <div className="empty enterprise-empty">{text}</div>;
}
function Loading({ error }: { error?: string }) {
  return (
    <div className="content">
      <div className={error ? "form-error" : "empty"}>
        {error || "Loading live workspace…"}
      </div>
    </div>
  );
}
function Modal({
  title,
  eyebrow,
  onClose,
  children,
}: {
  title: string;
  eyebrow: string;
  onClose: () => void;
  children: ReactNode;
}) {
  return (
    <div
      className="modal-backdrop"
      onMouseDown={(event) => event.target === event.currentTarget && onClose()}
    >
      <section className="modal enterprise-modal">
        <header>
          <div>
            <span className="kicker">{eyebrow}</span>
            <h2>{title}</h2>
          </div>
          <button className="icon-button" onClick={onClose}>
            <X />
          </button>
        </header>
        {children}
      </section>
    </div>
  );
}

export function PlatformFeeCommand() {
  const { csrfToken, user } = useAuth();
  const [rows, setRows] = useState<Row[]>([]),
    [message, setMessage] = useState("");
  async function load() {
    setRows(
      (await api<{ data: Row[] }>("/api/v1/enterprise/platform-fees")).data,
    );
  }
  useEffect(() => {
    void load();
  }, []);
  async function proof(row: Row, file: File) {
    if (!csrfToken) return;
    const response = await fetch(
      `/api/v1/enterprise/platform-fees/${row.id}/payment-proof`,
      {
        method: "POST",
        credentials: "include",
        headers: {
          "x-csrf-token": csrfToken,
          "content-type": file.type || "application/octet-stream",
          "x-file-name": file.name,
        },
        body: file,
      },
    );
    const result = await response.json();
    if (!response.ok) {
      setMessage(result.message || result.code);
      return;
    }
    setMessage("Payment proof submitted for Workwise verification.");
    await load();
  }
  async function verify(id: string, accepted: boolean) {
    if (!csrfToken) return;
    await api(`/api/v1/enterprise/platform-fees/${id}/verify`, csrfToken, {
      method: "POST",
      body: JSON.stringify({
        accepted,
        reason: accepted
          ? "Payment proof accepted"
          : "Payment proof requires correction",
      }),
    });
    await load();
  }
  return (
    <div className="content enterprise-page">
      <section className="page-heading">
        <div>
          <span className="kicker">Workwise platform billing</span>
          <h1>Platform Fee Invoices</h1>
          <p>
            The default fee is 2.5% of recorded sales. Owners submit proof; only
            the Workwise superadmin verifies payment.
          </p>
        </div>
      </section>
      {message && <div className="notice-banner">{message}</div>}
      <section className="metric-ribbon">
        <Metric
          icon={<FileSpreadsheet />}
          label="Invoices"
          value={rows.length}
          detail="Company scope"
        />
        <Metric
          icon={<WalletCards />}
          label="Amount due"
          value={money(
            rows
              .filter((x) => !["PAID", "REVERSED"].includes(x.status))
              .reduce((s, x) => s + Number(x.fee_amount), 0),
          )}
          detail="Recorded sales basis"
        />
        <Metric
          icon={<CalendarClock />}
          label="Proof pending"
          value={rows.filter((x) => x.status === "PAYMENT_SUBMITTED").length}
          detail="Superadmin action"
        />
        <Metric
          icon={<CheckCircle2 />}
          label="Paid"
          value={rows.filter((x) => x.status === "PAID").length}
          detail="Verified payments"
        />
      </section>
      <section className="panel table-panel">
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Invoice</th>
                <th>Business</th>
                <th>Period</th>
                <th>Sales basis</th>
                <th>Rate</th>
                <th>Fee</th>
                <th>Status / action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id}>
                  <td>{row.invoice_number || row.id.slice(0, 8)}</td>
                  <td>{row.trade_name}</td>
                  <td>
                    {row.period_start} — {row.period_end}
                  </td>
                  <td>{money(row.sales_basis)}</td>
                  <td>{Number(row.rate_percent)}%</td>
                  <td>
                    <strong>{money(row.fee_amount)}</strong>
                  </td>
                  <td>
                    <span className="status">{row.status}</span>
                    {!["PAID", "REVERSED", "PAYMENT_SUBMITTED"].includes(
                      row.status,
                    ) &&
                      user?.role !== "SUPERADMIN" && (
                        <label className="table-action file-button">
                          Add proof
                          <input
                            type="file"
                            accept="image/*,.pdf"
                            onChange={(event) =>
                              event.target.files?.[0] &&
                              void proof(row, event.target.files[0])
                            }
                          />
                        </label>
                      )}
                    {user?.role === "SUPERADMIN" &&
                      row.status === "PAYMENT_SUBMITTED" && (
                        <div className="row-buttons">
                          <button onClick={() => void verify(row.id, false)}>
                            Reject
                          </button>
                          <button onClick={() => void verify(row.id, true)}>
                            Accept
                          </button>
                        </div>
                      )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!rows.length && (
            <Empty text="No platform invoices have been generated." />
          )}
        </div>
      </section>
    </div>
  );
}
